import React, { memo, useMemo } from "react";
import PropTypes from "prop-types";

import { getSuggestionList, canUseSuggestions } from "widgets/others";

import { Editor } from "../../editors/editor";
import { getRowHeight } from "../../utils";

import styles from "./editors.css";

function totalWidthUntil(columns, col) {
  let width = 0;
  for (let i = 0; i < columns.length; i++) {
    if (columns[i].id === col) break;
    width += columns[i].width + 1;
  }
  return width;
}

export function DataSheetEditorsComponent(props) {
  const {
    absoluteTop,
    columns,
    cell,
    row,
    rowIndex,
    onChange,
    onStartEditing,
    onCompleted,
    onNext,
    onNextLine,
    onPrevious,
    totalWidth,
    fixedColumnWidth,
    tableGridTemplate,
    container,
    previewOption,
    customProps,
    instanceId,
    setIsFullScreen = () => {},
    isFullScreen,
    formConfig
  } = props;
  const SINGLE_ROW_HEIGHT = getRowHeight();
  let columnCondition =
    columns.reduce(
      (memo, column) => `${memo}:${column.id}_${column.width}`,
      ""
    ) +
    (cell ? `${cell[0]}:${cell[1]}` : ``) +
    rowIndex;

  const editorPos = useMemo(
    function setEditorPos() {
      if (rowIndex === null || rowIndex === undefined || !cell) {
        return {
          top: `${-1 * SINGLE_ROW_HEIGHT}px`,
          display: "none"
        };
      }
      const [col] = cell;
      if (!columns.find((column) => column.id === col)) {
        return {
          top: `${-1 * SINGLE_ROW_HEIGHT}px`,
          display: "none"
        };
      }
      let isCurrentColumnFixed = columns.find(
        (col) => col.fixed && col.id === cell[0]
      );
      const left = `${
        totalWidthUntil(columns, col) +
        (isCurrentColumnFixed ? 2 : 0) +
        SINGLE_ROW_HEIGHT * `${previewOption ? 2 : 1}`
      }px`;
      const height = `${SINGLE_ROW_HEIGHT}px`;
      const width = `${columns.find((column) => column.id === col).width}px`;
      const topValue = rowIndex * (SINGLE_ROW_HEIGHT + 1) + absoluteTop;
      const containerScrollLeft =
        (window.isRTL ? -1 : 1) * container.scrollLeft;
      let top = `${topValue}px`;
      let display = "grid";
      if (topValue < 0) {
        top = `${-1 * SINGLE_ROW_HEIGHT}px`;
        display = "none";
      }
      if (containerScrollLeft >= parseInt(left, 10) - fixedColumnWidth) {
        container.scrollLeft =
          (parseInt(left, 10) - fixedColumnWidth) * (window.isRTL ? -1 : 1);
      } else if (
        parseInt(left, 10) + parseInt(width, 10) >
        containerScrollLeft + container.offsetWidth - SINGLE_ROW_HEIGHT
      ) {
        container.scrollLeft =
          (parseInt(left, 10) +
            parseInt(width, 10) -
            container.offsetWidth +
            60) *
          (window.isRTL ? -1 : 1);
      }
      return { top, left, height, width, display };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnCondition]
  );

  const fixedColumns = useMemo(
    function getFixedColumns() {
      return columns.filter((column) => {
        return column.fixed;
      });
    },
    [columns]
  );

  const nonFixedColumns = useMemo(
    function getNonFixedColumns() {
      return columns.filter((column) => {
        return !column.fixed;
      });
    },
    [columns]
  );

  return (
    <React.Fragment>
      {cell && (
        <div
          className={styles.row}
          style={{
            top: editorPos.top,
            display: editorPos.display,
            width: `${totalWidth}px`,
            gridTemplateColumns: tableGridTemplate
          }}
        >
          <div className={styles.leftFixed}>
            {fixedColumns.map((column) => {
              return (
                <Editor
                  key={column.id}
                  column={column}
                  pos={editorPos}
                  row={row}
                  rowIndex={rowIndex}
                  show={cell[0] === column.id}
                  isVisible={cell[2].isVisible}
                  value={cell[0] === column.id && cell[2].value}
                  errors={cell[2].errors}
                  onChange={onChange}
                  onStartEditing={onStartEditing}
                  onCompleted={onCompleted}
                  onNext={onNext}
                  onNextLine={onNextLine}
                  onPrevious={onPrevious}
                  instanceId={instanceId}
                  suggestionList={
                    customProps?.idp &&
                    canUseSuggestions(column.Field) &&
                    getSuggestionList({
                      fieldId: column.id,
                      rowIndex,
                      tableId: column.modelConfig?.Model,
                      results: customProps.idp
                    })
                  }
                  setIsFullScreen={setIsFullScreen}
                  isFullScreen={isFullScreen}
                  customProps={customProps}
                  formConfig={formConfig}
                />
              );
            })}
          </div>
          {nonFixedColumns.map((column) => {
            return (
              <Editor
                column={column}
                key={column.id}
                pos={editorPos}
                row={row}
                rowIndex={rowIndex}
                show={cell[0] === column.id}
                isVisible={cell[2].isVisible}
                value={cell[0] === column.id && cell[2].value}
                errors={cell[2].errors}
                onChange={onChange}
                onStartEditing={onStartEditing}
                onCompleted={onCompleted}
                onNext={onNext}
                onNextLine={onNextLine}
                onPrevious={onPrevious}
                instanceId={instanceId}
                suggestionList={
                  customProps?.idp &&
                  canUseSuggestions(column.Field) &&
                  getSuggestionList({
                    fieldId: column.id,
                    rowIndex,
                    tableId: column.modelConfig?.Model,
                    results: customProps.idp
                  })
                }
                setIsFullScreen={setIsFullScreen}
                isFullScreen={isFullScreen}
                customProps={customProps}
                formConfig={formConfig}
              />
            );
          })}
        </div>
      )}
    </React.Fragment>
  );
}

DataSheetEditorsComponent.propTypes = {
  columns: PropTypes.array,
  pos: PropTypes.any,
  cell: PropTypes.array,
  row: PropTypes.object,
  onChange: PropTypes.func,
  onStartEditing: PropTypes.func,
  totalWidth: PropTypes.number,
  tableGridTemplate: PropTypes.string,
  container: PropTypes.element,
  customProps: PropTypes.object,
  rowIndex: PropTypes.number,
  absoluteTop: PropTypes.number,
  onCompleted: PropTypes.func,
  onNext: PropTypes.func,
  onNextLine: PropTypes.func,
  onPrevious: PropTypes.func,
  fixedColumnWidth: PropTypes.number,
  previewOption: PropTypes.bool,
  instanceId: PropTypes.string,
  setIsFullScreen: PropTypes.func,
  isFullScreen: PropTypes.bool,
  formConfig: PropTypes.object
};

export const DataSheetEditor = memo(DataSheetEditorsComponent);
